import {
  ChartArea,
  ChartColumn,
  ChartLine,
  ChartPie,
  ChartScatter,
  Plus,
  Sigma,
  type LucideIcon,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'

import type { ChartType, WidgetTypeName } from '@/api/types'
import { Button } from '@/shared/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/shared/ui/dropdown-menu'
import { useWidgetMutations } from '../mutations'

/**
 * What can be put on a board.
 *
 * The five charts are one widget type with a `chart_type` on it, and they are
 * offered as five choices rather than as a chart followed by a shape menu:
 * somebody adding a widget knows they want a pie, and making them add a bar
 * chart first and change it is a step that exists only because of how the model
 * happens to be shaped.
 *
 * Upstream shows the same list as a modal of illustrated cards. A menu is the
 * same choice without the round trip through a dialog, and the icons carry what
 * the illustrations did.
 */
const KINDS = [
  { key: 'summary', icon: Sigma, type: 'summary' },
  { key: 'bar', icon: ChartColumn, type: 'lithos_chart', chart: 'bar' },
  { key: 'line', icon: ChartLine, type: 'lithos_chart', chart: 'line' },
  { key: 'pie', icon: ChartPie, type: 'lithos_chart', chart: 'pie' },
  { key: 'area', icon: ChartArea, type: 'lithos_chart', chart: 'area' },
  { key: 'scatter', icon: ChartScatter, type: 'lithos_chart', chart: 'scatter' },
] as const satisfies readonly {
  /** Both the label's translation key and, translated, the new widget's title. */
  key: string
  icon: LucideIcon
  type: WidgetTypeName
  chart?: ChartType
}[]

export function AddWidget({
  dashboardId,
  onAdded,
  variant = 'outline',
}: {
  dashboardId: number
  /** Where to go once it exists -- its settings, since it is unconfigured. */
  onAdded: (widgetId: number) => void
  variant?: 'outline' | 'default'
}) {
  const { t } = useTranslation()
  const { create, isPending } = useWidgetMutations(dashboardId)

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant={variant} size="sm" disabled={isPending} className="on-surface">
          <Plus />
          {t('widget.add')}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {KINDS.map(({ key, icon: Icon, type, ...rest }) => (
          <DropdownMenuItem
            key={key}
            onSelect={() => {
              const chart = 'chart' in rest ? rest.chart : undefined
              create(
                {
                  // The name of the thing chosen, which is what upstream does
                  // too. A widget must have a title, and "Untitled" would be a
                  // worse first line on the card than "Bar chart".
                  title: t(`widget.${key}`),
                  type,
                  ...(chart === undefined ? {} : { chart_type: chart }),
                },
                (widget) => {
                  onAdded(widget.id)
                },
              )
            }}
          >
            <Icon />
            {t(`widget.${key}`)}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
